import dayjs from 'dayjs';
import { Button } from 'flowbite-react';
import { LiaLongArrowAltRightSolid } from 'react-icons/lia';
import { Img } from 'react-image';
import { browserName, isMobile } from 'react-device-detect';
import { DriveInviteeDocument, DriveInviteeTestStatusType } from 'types';
import { useEffect, useState } from 'react';
import { startDriveRegistrationAssessment } from 'src/apis/drive';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { useMediaQuery } from '@mui/material';
import { send } from 'process';
import { sendTrackingEvent } from 'src/utils/gtm';
import { useLocation } from 'react-router-dom';

const ConfirmationPage = ({ driveInvitee }: { driveInvitee: DriveInviteeDocument }) => {
	const toast = useToast();
	const location = useLocation();
	const queryClient = useQueryClient();
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);
	const isMobileView = useMediaQuery('(max-width: 960px)');

	useEffect(() => {
		const checkTime = () => {
			const startTime = driveInvitee?.userApplicationDetails?.testSlot?.startTime;
			if (startTime) {
				const currentTime = dayjs();
				const slotStartTime = dayjs(startTime);
				setIsButtonDisabled(currentTime.isBefore(slotStartTime));
			}
		};

		checkTime();
		const intervalId = setInterval(checkTime, 1000); // Check every second

		return () => clearInterval(intervalId); // Cleanup interval on component unmount
	}, [driveInvitee]);

	const startDriveRegistrationAssessmentMutation = useMutation(startDriveRegistrationAssessment, {
		onSuccess: data => {
			queryClient.refetchQueries({
				queryKey: ['getDriveRegistrationApplication'],
				exact: false,
			});
			window.location.href = data?.url;
		},
		onError: error => {
			toast({
				title: 'Something went wrong!',
				type: 'error',
			});
		},
	});

	const handleStartAssessment = (isMock: boolean) => {
		if (driveInvitee?.userApplicationDetails?.mainTestLink && !isMock) {
			window.location.href = driveInvitee?.userApplicationDetails?.mainTestLink;
			return;
		}
		if (driveInvitee?.userApplicationDetails?.mockTestLink && isMock) {
			window.location.href = driveInvitee?.userApplicationDetails?.mockTestLink;
			return;
		}
		startDriveRegistrationAssessmentMutation.mutate({
			driveApplicationId: driveInvitee?._id,
			isMock: isMock,
		});
	};

	return (
		<>
			<div className="flex flex-col mx-auto rounded-[8px] justify-center items-center py-4">
				<div className="max-w-md w-full flex flex-col items-center text-center">
					<div className="max-w-[48px] max-h-[48px] mx-auto mt-[20px] md:mt-[64px]">
						<img src="/assets/assessmentcompleted/circle.svg" className="w-full h-full" />
					</div>
					<h1 className="text-[20px] font-inter font-[700] text-[#0A0103] leading-[28px] mt-4">All Set!</h1>
					<div className="space-y-2 text-[#544D4F] text-[16px] leading-[24px] font-[600] mt-3">
						Your test has been scheduled
						<br />
						for{' '}
						<span className="text-[#7777E7] text-[16px] font-inter font-[600] leading-[24px]">
							{dayjs(driveInvitee?.userApplicationDetails?.testSlot?.startTime).format('DD MMM, hh:mm a')}
						</span>
						<br />A calendar invite has been sent to your email.
						<br />
						Add to your calendar for timely reminders.
					</div>
					{browserName !== 'Chrome' ? (
						<div className="flex items-center justify-center gap-2 text-sm text-gray-700 bg-gray-50 px-4 rounded-lg w-full mt-[31px]">
							<Img
								src="https://masai-website-images.s3.ap-south-1.amazonaws.com/image59_ezgif_com_png_to_webp_converter_01d9085f13.webp"
								alt="Chrome Logo"
								width={32}
								height={32}
							/>
							<div className="text-left text-[#3B3435] text-xs font-inter font-[400]">
								Please install the latest Chrome Browser
								<br />
								for the assessment.{' '}
								<a
									href="https://www.google.com/chrome/"
									target="_blank"
									className="text-[#7777E7] font-inter font-[400] underline"
								>
									Use Chrome
								</a>
							</div>
						</div>
					) : null}
					{/* Updated button container with correct styling */}
					<div className="flex gap-4 w-full max-w-[600px] items-center justify-center mt-[30px]">
						<Button
							outline
							color="blue"
							type="button"
							size={isMobileView ? 'md' : 'lg'}
							className="[&>span]:px-2 md:[&>span]:px-4 w-full flex-1 bg-[#1A56DB] [&>span]:text-[#1A56DB] [&>span]:rounded-[0.4rem] p-[1px]"
							disabled={driveInvitee?.userApplicationDetails?.mockTestStatus === DriveInviteeTestStatusType.COMPLETED}
							onClick={() => {
								handleStartAssessment(true)
								sendTrackingEvent({
									event: 'recr_dashboard_active_companies',
									eventLabel: location.pathname,
								});
							}}
						>
							{!driveInvitee?.userApplicationDetails?.mockTestLink ? (
								<div className="absolute -top-3 h-fit right-0 bg-[#FFDB66] text-[#3B3435] text-[10px] px-[5px] py-[2px] leading-[14.286px] rounded-full whitespace-nowrap">
									Only 1 mock available
								</div>
							) : null}
							{driveInvitee?.userApplicationDetails?.mockTestLink &&
								driveInvitee?.userApplicationDetails?.mockTestStatus !== DriveInviteeTestStatusType.COMPLETED
								? 'Continue Mock'
								: 'Practice Mock'}
						</Button>

						<div className="w-full flex-1">
							<Button
								type="button"
								color="purplePlaced"
								size={isMobileView ? 'md' : 'lg'}
								className="[&>span]:px-2 md:[&>span]:px-4 [&>span]:w-full text-center [&>span]:justify-center whitespace-pre w-full p-[1px]"
								disabled={isMobile || isMobileView || isButtonDisabled}
								onClick={() => {
									handleStartAssessment(false);
									sendTrackingEvent({
										event: 'recr_dashboard_active_companies',
										eventLabel: location.pathname,
									});
								}}
							>
								{driveInvitee?.userApplicationDetails?.mainTestLink &&
									driveInvitee?.userApplicationDetails?.mainTestStatus !== DriveInviteeTestStatusType.COMPLETED
									? 'Continue Assessment'
									: 'Start Assessment'}
								<LiaLongArrowAltRightSolid className="ml-[4px]" size="20" />
							</Button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default ConfirmationPage;
